import type {
  CreateReturnRequestResponse,
  NotificationItem,
  ReturnRequestDetail,
  SubmitReturnRequestPayload
} from '../types/vipMp';
import { apiJson } from './httpClient';

export async function postReturnRequest(
  payload: SubmitReturnRequestPayload
): Promise<CreateReturnRequestResponse> {
  return apiJson<CreateReturnRequestResponse>('/return-requests', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  });
}

export async function postResubmit(
  returnRequestId: number,
  body: {
    submitterName: string;
    submitterEmail: string;
    filedByName?: string;
    filedByEmail?: string;
    filedByPersona?: string;
    partnerComments: string | null;
    managerName: string | null;
    managerEmail: string | null;
    adobeSalesOrderNumber?: string | null;
    replacementOrderNumber?: string | null;
    orderDate?: string | null;
    customerMarketplaceAccountId?: string;
    customerName?: string;
    geoCode?: string;
    returnTypeCode?: string;
    reasonCode?: string;
    timeframeCode?: string;
    lineItems: SubmitReturnRequestPayload['lineItems'];
    attachments: SubmitReturnRequestPayload['attachments'];
  }
): Promise<{ success?: boolean }> {
  return apiJson(`/return-requests/${returnRequestId}/resubmit`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body)
  });
}

export async function getPartnerSubmissions(params: {
  partnerEmail?: string;
  partnerOrgId?: number;
  statusCode?: string;
  managerName?: string;
}): Promise<Record<string, unknown>[]> {
  return apiJson<Record<string, unknown>[]>('/partner/submissions', {
    query: params
  });
}

export async function getReturnRequestDetail(id: number): Promise<ReturnRequestDetail> {
  return apiJson<ReturnRequestDetail>(`/return-requests/${id}`);
}

export async function getAttachmentDownloadUrl(
  returnRequestId: number,
  attachmentId: number
): Promise<{ url: string; fileName?: string }> {
  return apiJson<{ url: string; fileName?: string }>(
    `/return-requests/${returnRequestId}/attachments/${attachmentId}/download-url`
  );
}

export async function postComment(
  returnRequestId: number,
  body: {
    commentText: string;
    authorPersonaCode: string;
    authorName: string;
    authorEmail?: string | null;
    visibleToPartner: boolean;
  }
): Promise<{ success?: boolean }> {
  return apiJson(`/return-requests/${returnRequestId}/comments`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body)
  });
}

export type CommunicationAttachment = {
  fileName: string;
  storageKey: string;
  fileUrl: string;
  fileType: string;
};

export type TaggedPerson = { name: string; email: string };

export type CommunicationMessage = {
  communicationId: number;
  returnRequestId: number;
  authorPersonaCode: string;
  authorName: string;
  authorEmail: string | null;
  messageText: string;
  taggedPeople: TaggedPerson[];
  attachments: CommunicationAttachment[];
  createdUtc: string;
};

export async function getCommunications(
  returnRequestId: number,
  params: { requesterPersona: string; requesterEmail?: string }
): Promise<CommunicationMessage[]> {
  return apiJson<CommunicationMessage[]>(`/return-requests/${returnRequestId}/communications`, { query: params });
}

export async function postCommunication(
  returnRequestId: number,
  body: {
    authorPersonaCode: string;
    authorName: string;
    authorEmail?: string | null;
    messageText: string;
    taggedPeople: TaggedPerson[];
    attachments?: Array<{ fileName: string; fileContentBase64: string; fileType: string }>;
  }
): Promise<{ success: boolean }> {
  return apiJson<{ success: boolean }>(`/return-requests/${returnRequestId}/communications`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body)
  });
}

export async function getCommunicationAttachmentUrl(
  returnRequestId: number,
  storageKey: string,
  params: { requesterPersona: string; requesterEmail?: string }
): Promise<{ url: string }> {
  return apiJson<{ url: string }>(`/return-requests/${returnRequestId}/communication-attachment-url`, {
    query: { key: storageKey, ...params }
  });
}

export async function getNotifications(params: { role: 'partner'; email?: string; limit?: number }) {
  return apiJson<{ items: NotificationItem[] }>('/notifications', { query: params });
}

export type PartnerDirectoryRow = {
  partnerName: string;
  soldToId: string | null;
  assignmentGroup: string | null;
  country: string | null;
};

export async function getPartnerDirectory(): Promise<PartnerDirectoryRow[]> {
  return apiJson<PartnerDirectoryRow[]>('/partner-directory');
}

// ── Module-level 15-min cache for @mention people search ─────────────────────

export type CachedPerson = { name: string; email: string; persona?: string };
const CACHE_TTL = 15 * 60 * 1000;

let _peopleCache: CachedPerson[] | null = null;
let _peopleCacheExpiry = 0;

export async function getPeopleCached(): Promise<CachedPerson[]> {
  if (_peopleCache && Date.now() < _peopleCacheExpiry) return _peopleCache;
  const list = await apiJson<CachedPerson[]>('/people-all');
  _peopleCache = Array.isArray(list) ? list : [];
  _peopleCacheExpiry = Date.now() + CACHE_TTL;
  return _peopleCache;
}

let _orgMembersCache: CachedPerson[] | null = null;
let _orgMembersCacheExpiry = 0;

export async function getPartnerOrgMembersCached(): Promise<CachedPerson[]> {
  if (_orgMembersCache && Date.now() < _orgMembersCacheExpiry) return _orgMembersCache;
  const list = await apiJson<CachedPerson[]>('/partner/org-members');
  _orgMembersCache = Array.isArray(list) ? list : [];
  _orgMembersCacheExpiry = Date.now() + CACHE_TTL;
  return _orgMembersCache;
}

export function filterPeople(list: CachedPerson[], q: string): CachedPerson[] {
  const norm = q.trim().toLowerCase();
  if (!norm) return [];
  return list.filter(p =>
    p.name.toLowerCase().includes(norm) || p.email.toLowerCase().includes(norm)
  ).slice(0, 10);
}
